type ClarityApi = {
  init: (projectId: string) => void;
  identify?: (customUserId: string) => void;
  setTag?: (key: string, value: string | string[]) => void;
};

const CLARITY_DISABLE_TOKENS = new Set(['false', '0', 'no', 'off', 'disabled']);

function isClarityEnabled(): boolean {
  // Opt-out only: enabled unless explicitly set to a false token.
  const rawFlag = import.meta.env.VITE_ENABLE_CLARITY;
  if (rawFlag === undefined) return true;
  const normalized = String(rawFlag).trim().toLowerCase();
  return !CLARITY_DISABLE_TOKENS.has(normalized);
}

const CLARITY_PROJECT_ID = 'qd7c3znho4';

function getClarityProjectId(): string | null {
  // Allow env override; fall back to hardcoded project ID.
  const raw = import.meta.env.VITE_CLARITY_PROJECT_ID;
  if (raw !== undefined) {
    const trimmed = String(raw).trim();
    if (trimmed.length > 0) return trimmed;
  }
  return CLARITY_PROJECT_ID;
}

function isClarityInitialized(): boolean {
  return window.__ieHubClarityInitialized === true;
}

function markClarityInitialized(): void {
  window.__ieHubClarityInitialized = true;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

let clarityApiPromise: Promise<ClarityApi | null> | null = null;

async function loadClarityApi(): Promise<ClarityApi | null> {
  if (clarityApiPromise) return clarityApiPromise;
  clarityApiPromise = (async () => {
    try {
      const mod = await import('@microsoft/clarity');
      const candidate: unknown =
        isObject(mod) && 'default' in mod ? (mod as { default?: unknown }).default ?? mod : mod;

      if (!isObject(candidate)) return null;

      const init = candidate.init;
      const identify = candidate.identify;
      const setTag = candidate.setTag;

      if (typeof init !== 'function') return null;
      if (identify !== undefined && typeof identify !== 'function') return null;
      if (setTag !== undefined && typeof setTag !== 'function') return null;

      return {
        init: init as ClarityApi['init'],
        identify: identify as ClarityApi['identify'],
        setTag: setTag as ClarityApi['setTag'],
      };
    } catch {
      return null;
    }
  })();
  return clarityApiPromise;
}

export async function initClarity(): Promise<void> {
  try {
    const projectId = getClarityProjectId();
    if (!projectId) return;
    if (!isClarityEnabled()) return;
    if (isClarityInitialized()) return;

    const Clarity = await loadClarityApi();
    if (!Clarity) return;

    Clarity.init(projectId);
    markClarityInitialized();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Clarity init failed', e);
  }
}

export async function identifyClarityUser(
  user: { userId?: number | string | null | undefined } | number | string | null | undefined
): Promise<void> {
  try {
    if (!isClarityInitialized()) {
      await initClarity();
      if (!isClarityInitialized()) return;
    }

    const userId =
      typeof user === 'object' && user !== null ? user.userId : user;

    if (userId === null || userId === undefined) return;
    const id = String(userId).trim();
    if (!id) return;

    const Clarity = await loadClarityApi();
    if (!Clarity?.identify) return;

    // No PII: use userId only.
    Clarity.identify(id);

    // Also set as an unmasked custom tag so support can see the raw
    // userId on the session card (Clarity masks Custom IDs by default).
    // Try the SDK wrapper first; fall back to Clarity's global command API
    // (window.clarity('set', key, value)) which is the documented stable surface.
    let tagged = false;
    if (Clarity.setTag) {
      try { Clarity.setTag('userId', id); tagged = true; } catch {}
    }
    if (!tagged) {
      try {
        const globalClarity = (window as unknown as { clarity?: (cmd: string, ...args: unknown[]) => void }).clarity;
        if (typeof globalClarity === 'function') {
          globalClarity('set', 'userId', id);
        }
      } catch {}
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Clarity identify failed', e);
  }
}
